import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL, APP_CONFIG } from '../../config/app';
import type { ContactFormData, ServiceType } from './types';
import { BUDGET_OPTIONS } from './types';
import { CityAutocomplete } from './CityAutocomplete';
import { FormField } from './FormField';
import { trackLead, contactCtaClick } from '../../utils/analytics';
import { getServiceFromPath } from '../../utils/serviceFromPath';

const SERVICE_TYPES: readonly ServiceType[] = ['depannage', 'site-web', 'reparation-telephone', 'autre'];

const isServiceType = (value: string | null): value is ServiceType =>
  value !== null && (SERVICE_TYPES as readonly string[]).includes(value);

const SITE_PRO_OFFER = 'site-pro';
const SITE_PRO_MESSAGE_PLACEHOLDER =
  'Votre activité et ce que vous attendez du site (ex : plombier à Annemasse, je veux être trouvé sur Google)';

const WHATSAPP_URL = APP_CONFIG.contact.whatsappUrl;

const initialState: ContactFormData = {
  service: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  city: '',
  budget: undefined,
  message: '',
  consent: false,
};

/** Bornes alignées sur backend/app/api/contact/route.ts. */
const MESSAGE_MAX = 4000;

type FieldName = 'service' | 'firstName' | 'phone' | 'email' | 'message' | 'consent';
type FormErrors = Partial<Record<FieldName, string>>;

/** Ordre d'affichage des champs : sert à placer le focus sur le premier champ invalide. */
const FIELD_ORDER: readonly FieldName[] = ['service', 'firstName', 'phone', 'email', 'message', 'consent'];

const FIELD_IDS: Record<FieldName, string> = {
  service: 'contact-service',
  firstName: 'contact-first-name',
  phone: 'contact-phone',
  email: 'contact-email',
  message: 'contact-message',
  consent: 'contact-consent',
};

const PHONE_ALLOWED = /^[+\d\s.\-()]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validateField = (field: FieldName, form: ContactFormData): string | undefined => {
  switch (field) {
    case 'service':
      return form.service ? undefined : 'Choisissez le service concerné.';
    case 'firstName':
      if (!form.firstName.trim()) return 'Indiquez votre prénom.';
      return form.firstName.trim().length > 80 ? 'Prénom trop long (80 caractères maximum).' : undefined;
    case 'phone': {
      const phone = form.phone.trim();
      if (!phone) return 'Indiquez votre numéro de téléphone pour que je puisse vous rappeler.';
      if (phone.length > 30 || !PHONE_ALLOWED.test(phone) || phone.replace(/\D/g, '').length < 8) {
        return 'Numéro invalide : 8 chiffres minimum (chiffres, espaces et + . - ( ) acceptés).';
      }
      return undefined;
    }
    case 'email':
      return !form.email.trim() || (form.email.trim().length <= 254 && EMAIL_PATTERN.test(form.email.trim()))
        ? undefined
        : 'Adresse email invalide (ex : jean@exemple.com).';
    case 'message':
      if (form.message.trim().length < 2) return 'Décrivez brièvement votre besoin.';
      return form.message.trim().length > MESSAGE_MAX ? `Message trop long (${MESSAGE_MAX} caractères maximum).` : undefined;
    case 'consent':
      return form.consent ? undefined : 'Acceptez la politique de confidentialité pour envoyer votre demande.';
  }
};

const BASE_INPUT_CLASS =
  'w-full bg-surface border rounded-lg px-3 py-2.5 text-sm text-fg placeholder-gray-500 focus:outline-none focus:border-accent/60 transition-colors';

export const ContactForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const serviceParam = searchParams.get('service');
  const messageParam = searchParams.get('message');
  const isSiteProOffer = searchParams.get('offre') === SITE_PRO_OFFER;
  const [form, setForm] = useState<ContactFormData>(() => ({
    ...initialState,
    service: isServiceType(serviceParam) ? serviceParam : '',
    message: (messageParam ?? '').slice(0, MESSAGE_MAX),
  }));
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = <K extends keyof ContactFormData>(field: K, value: ContactFormData[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /** Valide un champ (au blur, ou à la saisie si le champ est déjà en erreur). */
  const validateOne = (field: FieldName, data: ContactFormData = form) =>
    setErrors((prev) => ({ ...prev, [field]: validateField(field, data) }));

  const update = <K extends FieldName & keyof ContactFormData>(field: K, value: ContactFormData[K]) => {
    set(field, value);
    if (errors[field]) validateOne(field, { ...form, [field]: value });
  };

  const inputClassFor = (field: FieldName) =>
    `${BASE_INPUT_CLASS} ${errors[field] ? 'border-red-400' : 'border-line'}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: FormErrors = {};
    for (const field of FIELD_ORDER) {
      const error = validateField(field, form);
      if (error) nextErrors[field] = error;
    }
    setErrors(nextErrors);
    const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field]);
    if (firstInvalid) {
      document.getElementById(FIELD_IDS[firstInvalid])?.focus();
      return;
    }
    setLoading(true);
    try {
      // Les champs facultatifs vides sont envoyés en chaîne vide (traités comme absents côté API).
      const payload: ContactFormData = {
        ...form,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        message: form.message.trim(),
      };
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur envoi');
      trackLead(form.service);
      setSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center gap-4 py-12 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-accent-strong/20 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-accent-soft" />
        </div>
        <h3 className="text-fg font-bold text-lg">Message envoyé !</h3>
        <p role="status" className="text-fg-muted text-sm max-w-xs">
          Je vous rappelle en moins de 2h (devis de site web : sous 48h).
          {form.email.trim() && ' Un email de confirmation vous a été envoyé.'}
        </p>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            contactCtaClick({
              channel: 'whatsapp',
              service: form.service || getServiceFromPath(window.location.pathname),
              cta_location: 'form',
            })
          }
          className="mt-2 inline-flex min-h-11 items-center gap-2 px-5 py-2.5 border border-line-strong text-fg rounded-full text-sm font-bold hover:border-fg-subtle transition-colors"
        >
          Besoin d'une réponse immédiate ? WhatsApp →
        </a>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {/* Honeypot anti-spam */}
      <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <FormField id={FIELD_IDS.service} label="Je cherche..." error={errors.service}>
        {(a11y) => (
          <select
            {...a11y}
            value={form.service}
            onChange={(e) => update('service', e.target.value as ServiceType | '')}
            onBlur={() => validateOne('service')}
            className={inputClassFor('service')}
          >
            <option value="" disabled>Sélectionner un service</option>
            <option value="depannage">🔧 Dépannage informatique</option>
            <option value="site-web">🌐 Création de site web</option>
            <option value="reparation-telephone">📱 Réparation téléphone</option>
            <option value="autre">❓ Autre demande</option>
          </select>
        )}
      </FormField>

      {/* Champ budget conditionnel (site web uniquement) */}
      <AnimatePresence>
        {form.service === 'site-web' && !isSiteProOffer && (
          <motion.div
            key="budget"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FormField id="contact-budget" label="Budget approximatif" optional>
              {(a11y) => (
                <select
                  {...a11y}
                  value={form.budget ?? ''}
                  onChange={(e) =>
                    set('budget', e.target.value as typeof BUDGET_OPTIONS[number]['value'] | undefined)
                  }
                  className={`${BASE_INPUT_CLASS} border-line`}
                >
                  <option value="">Non défini</option>
                  {BUDGET_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
            </FormField>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Prénom + Nom */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField id={FIELD_IDS.firstName} label="Prénom" error={errors.firstName}>
          {(a11y) => (
            <input
              {...a11y}
              type="text"
              autoComplete="given-name"
              placeholder="Jean"
              value={form.firstName}
              onChange={(e) => update('firstName', e.target.value)}
              onBlur={() => validateOne('firstName')}
              className={inputClassFor('firstName')}
            />
          )}
        </FormField>
        <FormField id="contact-last-name" label="Nom" optional>
          {(a11y) => (
            <input
              {...a11y}
              type="text"
              autoComplete="family-name"
              placeholder="Dupont"
              value={form.lastName}
              maxLength={80}
              onChange={(e) => set('lastName', e.target.value)}
              className={`${BASE_INPUT_CLASS} border-line`}
            />
          )}
        </FormField>
      </div>

      {/* Téléphone */}
      <FormField id={FIELD_IDS.phone} label="Téléphone" error={errors.phone}>
        {(a11y) => (
          <input
            {...a11y}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+33 6 ..."
            value={form.phone}
            onChange={(e) => update('phone', e.target.value)}
            onBlur={() => validateOne('phone')}
            className={inputClassFor('phone')}
          />
        )}
      </FormField>

      {/* Email */}
      <FormField id={FIELD_IDS.email} label="Email" optional error={errors.email}>
        {(a11y) => (
          <input
            {...a11y}
            type="email"
            autoComplete="email"
            placeholder="jean@exemple.com"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            onBlur={() => validateOne('email')}
            className={inputClassFor('email')}
          />
        )}
      </FormField>

      {/* Ville (autocomplétion Google Places, facultative) */}
      <div>
        <label htmlFor="contact-city" className="block text-xs font-medium text-fg-muted uppercase tracking-wider mb-1.5">
          Ville <span className="ml-1 normal-case tracking-normal text-fg-subtle">(facultatif)</span>
        </label>
        <CityAutocomplete id="contact-city" value={form.city} onChange={(city) => set('city', city)} />
      </div>

      {/* Message */}
      <FormField id={FIELD_IDS.message} label="Message" error={errors.message}>
        {(a11y) => (
          <textarea
            {...a11y}
            rows={4}
            placeholder={isSiteProOffer ? SITE_PRO_MESSAGE_PLACEHOLDER : 'Décrivez votre besoin...'}
            value={form.message}
            onChange={(e) => update('message', e.target.value)}
            onBlur={() => validateOne('message')}
            className={`${inputClassFor('message')} resize-none`}
          />
        )}
      </FormField>

      {/* Consentement RGPD */}
      <div>
        <label className="flex min-h-11 items-start gap-2.5 cursor-pointer">
          <input
            id={FIELD_IDS.consent}
            type="checkbox"
            checked={form.consent}
            aria-invalid={Boolean(errors.consent)}
            aria-describedby={errors.consent ? 'contact-consent-error' : undefined}
            onChange={(e) => update('consent', e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-accent cursor-pointer flex-shrink-0"
          />
          <span className="text-xs text-fg-subtle leading-relaxed">
            J'accepte que mes données soient utilisées pour traiter ma demande conformément à la{' '}
            <Link to="/politique-de-confidentialite" className="text-accent-soft hover:underline">
              politique de confidentialité
            </Link>.
          </span>
        </label>
        {errors.consent && (
          <p id="contact-consent-error" role="alert" className="mt-1.5 text-xs text-red-400">
            {errors.consent}
          </p>
        )}
      </div>

      {/* CTA bouton */}
      <button
        type="submit"
        disabled={loading}
        className="relative w-full min-h-11 py-3 px-6 bg-accent-strong hover:bg-accent-hover text-white font-bold rounded-full text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent" />
        <span className="relative z-10 flex items-center justify-center gap-2">
          {loading ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Send size={15} />
          )}
          {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
        </span>
      </button>

      <p className="text-xs text-fg-subtle text-center">Données confidentielles · Réponse en moins de 2h</p>
    </form>
  );
};
